import { useMemo } from 'react';
import { Flex, Stack, Select, ActionIcon } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import { Text } from '~/domains/design-system/Text';
import type { SlateConfigurationRowProps } from './types';
import { getAvailableSlateOptions, getAvailablePercentageOptions } from './utils';

export function SlateConfigurationRow({
  slate,
  slateOptions,
  percentageOptions,
  onSlateChange,
  onPercentageChange,
  onDelete,
  canDelete,
  errors,
  index,
  allSlates,
}: SlateConfigurationRowProps) {
  const { t } = useTranslation('contest');

  const availableSlateOptions = useMemo(
    () => getAvailableSlateOptions(slateOptions, allSlates, index),
    [slateOptions, allSlates, index]
  );

  const availablePercentageOptions = useMemo(
    () => getAvailablePercentageOptions(percentageOptions, allSlates, index),
    [percentageOptions, allSlates, index]
  );

  return (
    <Flex /* eslint-disable-line local-rules/use-theme-tokens */ gap={16} direction="row" mt={16}>
      <Stack w="100%">
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} w="100%">
          <Text variant="body-medium" color="gray.6">
            {t('manage.common.fields.flexibleDoublePicksField.enabledIn')}
          </Text>
          <Select
            value={slate.slateId}
            onChange={(value) => value && onSlateChange(slate.id, value)}
            data={availableSlateOptions}
            placeholder="Select week"
            error={errors?.slateId}
          />
        </Stack>

        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} w="100%">
          <Text variant="body-medium" color="gray.6">
            {t(
              'manage.common.fields.flexibleDoublePicksField.ifThisPercentageOfRemainingEntriesRemain'
            )}
          </Text>
          <Select
            value={slate.percentage?.toString() || ''}
            onChange={(value) => value && onPercentageChange(slate.id, Number(value))}
            data={availablePercentageOptions.map((opt) => ({
              ...opt,
              value: opt.value.toString(),
            }))}
            placeholder="Select percentage"
            error={errors?.percentage}
          />
        </Stack>
      </Stack>

      {canDelete && (
        <ActionIcon variant="subtle" color="gray2.1" size="md" onClick={() => onDelete(slate.id)}>
          <IconTrash size={16} />
        </ActionIcon>
      )}
    </Flex>
  );
}
